import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import SubHeader from "components/common/SubHeader";
import NotificationItem from "components/alert/NotificationItem";
import CommunityNotificationItem from "components/alert/CommunityNotificationItem";
import NoticeItem from "components/alert/NoticeItem";
import DateGroup from "components/alert/DateGroup";
import useRefreshCallback from "hooks/useRefreshCallback";
import { getNotifications, markNotificationAsRead, markAllNotificationsAsRead, postNotificationAction } from "api/user";

import {
    getCommunityNotifications,
    markCommunityNotificationAsRead,
    markAllCommunityNotificationsAsRead,
    getCommunityUnreadCount,
} from "api/community";
import { getNotices } from "api/notice";
import useUserStore from "@store/useUserStore";
import { useSnackbar } from "contexts/SnackbarContext";
import { getPublicUserKey } from "utils/userIdentity";

import "assets/scss/pages/alertPage.scss";

const clearAppBadge = () => {
    try {
        if (window.nativeApp?.clearBadge) {
            window.nativeApp.clearBadge();
        } else if (window.Android?.clearNotifications) {
            window.Android.clearNotifications();
        }
    } catch {}
};

// 로컬 타임존 기준 YYYY-MM-DD 키 (UTC 변환 시 자정 부근 날짜가 하루 밀리는 문제 방지)
const toLocalDateKey = (value) => {
    const d = new Date(value);
    if (isNaN(d.getTime())) return "";
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
};

// Group notifications by date (공지사항은 published_at 기준으로 그룹핑)
const groupByDate = (items, dateField = "created_at") => {
    return items.reduce((acc, item) => {
        const date = toLocalDateKey(item[dateField]);
        if (!acc[date]) {
            acc[date] = [];
        }
        acc[date].push(item);
        return acc;
    }, {});
};

export const buildCommunityNotificationUrl = (notification) => {
    const { type, target_type, target_id, actor, post_id, comment_id } = notification;
    const actorId = getPublicUserKey(actor);

    // follow 알림의 이동 대상은 actor 뿐이다. actor.public_id 가 없으면
    // 내부 식별자가 담긴 target_id 분기로 흘러가지 않도록 여기서 끝낸다.
    if (type === "follow") {
        return actorId ? `/community/user/${encodeURIComponent(actorId)}` : null;
    }

    if (target_type === "user" && target_id) {
        return `/community/user/${encodeURIComponent(target_id)}`;
    }

    const postId = post_id || (target_type === "post" ? target_id : null);
    const commentId = comment_id || (target_type === "comment" ? target_id : null);

    // 댓글 단독 라우트가 없어 부모 게시글 id가 없으면 이동할 수 없다.
    // 서버가 post_id를 항상 내려준다는 보장이 없으므로(의도된 막다른 길 아님)
    // 여기서는 null을 반환하고 호출부에서 에러 안내로 폴백한다. 근본 해소는 백엔드 정합.
    if (!postId) return null;

    const commentQuery = commentId ? `?comment=${encodeURIComponent(commentId)}` : "";
    return `/community/${encodeURIComponent(postId)}${commentQuery}`;
};

const SHARING_PREFIX = "/server/buccl_sharing";

const normalizeLessonRequestNotificationPath = (path) => {
    const withoutSharingPrefix = path.startsWith(SHARING_PREFIX)
        ? path.slice(SHARING_PREFIX.length) || "/"
        : path;
    const legacyStudentMatch = withoutSharingPrefix.match(/^\/lesson-request\/(\d+)(\?.*)?$/);
    if (legacyStudentMatch) {
        return `/lesson-requests/${legacyStudentMatch[1]}${legacyStudentMatch[2] || ""}`;
    }
    const legacyInstructorMatch = withoutSharingPrefix.match(/^\/instructor\/lesson-requests\/(\d+)(\?.*)?$/);
    if (legacyInstructorMatch) {
        return `/lesson-requests/${legacyInstructorMatch[1]}${legacyInstructorMatch[2] || ""}`;
    }
    return withoutSharingPrefix;
};

const getNotificationValue = (notification, ...keys) => {
    const extraData = notification.extra_data || notification.extraData || {};
    for (const key of keys) {
        const value = notification[key] ?? extraData[key];
        if (value !== undefined && value !== null && value !== "") return value;
    }
    return null;
};

export const buildReservationNotificationUrl = (notification = {}) => {
    const notificationType = String(notification.notification_type || notification.notificationType || notification.type || "").toUpperCase();
    const requestId = getNotificationValue(notification, "request_id", "requestId");
    const objectType = String(notification.related_object_type || notification.relatedObjectType || "").toLowerCase();
    const objectId = notification.related_object_id || notification.relatedObjectId || requestId;

    if (notificationType.startsWith("LESSON_REQUEST_") && requestId) {
        return `/lesson-requests/${encodeURIComponent(requestId)}`;
    }

    if (objectId && (objectType === "lessonrequest" || objectType === "lesson_request" || requestId)) {
        return `/lesson-requests/${encodeURIComponent(objectId)}`;
    }

    const raw = notification.action_url || notification.path || notification.deeplink || notification.deep_link || notification.actionUrl;
    if (!raw) return null;
    try {
        const url = new URL(raw, window.location.origin);
        return normalizeLessonRequestNotificationPath(`${url.pathname}${url.search}`);
    } catch {
        const path = String(raw);
        return path.startsWith("/") ? normalizeLessonRequestNotificationPath(path) : null;
    }
};

export const resolveReservationNotificationUrl = async (notification = {}) => {
    return buildReservationNotificationUrl(notification);
};

const AlertPage = () => {
    const [activeTab, setActiveTab] = useState("reservation");
    const [reservationNotifications, setReservationNotifications] = useState([]);
    const [communityNotifications, setCommunityNotifications] = useState([]);
    const [notices, setNotices] = useState([]);
    const [reservationUnread, setReservationUnread] = useState(0);
    const [communityUnread, setCommunityUnread] = useState(0);

    const navigate = useNavigate();
    const userInfo = useUserStore((state) => state.userInfo);
    const setReservationUnreadCount = useUserStore((state) => state.setReservationUnreadCount);
    const setCommunityUnreadCount = useUserStore((state) => state.setCommunityUnreadCount);
    const { showError, showSuccess } = useSnackbar();

    const notifications = activeTab === "reservation" ? reservationNotifications : communityNotifications;
    const groupedNotifications = groupByDate(notifications);
    const currentUnread = activeTab === "reservation" ? reservationUnread : communityUnread;

    const loadReservationNotifications = useCallback(async () => {
        try {
            const res = await getNotifications();
            const list = res?.results ?? res ?? [];
            const sorted = [...list].sort(
                (a, b) => new Date(b.created_at) - new Date(a.created_at)
            );
            setReservationNotifications(sorted);
            const unread = res?.unread_count ?? sorted.filter((n) => !n.is_read).length;
            setReservationUnread(unread);
            setReservationUnreadCount(unread);
        } catch {
            showError("예약 알림을 불러오지 못했습니다");
        }
    }, [setReservationUnreadCount, showError]);

    const loadCommunityNotifications = useCallback(async () => {
        try {
            const res = await getCommunityNotifications();
            const list = res?.data ?? res?.results ?? res ?? [];
            const sorted = [...list].sort(
                (a, b) => new Date(b.created_at) - new Date(a.created_at)
            );
            setCommunityNotifications(sorted);
            const countRes = await getCommunityUnreadCount();
            const unread = countRes?.data?.unread_count ?? countRes?.unread_count ?? sorted.filter((n) => !n.is_read).length;
            setCommunityUnread(unread);
            setCommunityUnreadCount(unread);
        } catch {
            showError("커뮤니티 알림을 불러오지 못했습니다");
        }
    }, [setCommunityUnreadCount, showError]);

    const loadNotices = useCallback(async () => {
        try {
            const res = await getNotices();
            // BE가 중요 공지 우선 → 게시일 최신순으로 정렬해 내려줌 (FE 재정렬 불필요)
            setNotices(res?.results ?? res ?? []);
        } catch {
            showError("공지사항을 불러오지 못했습니다");
        }
    }, [showError]);

    const loadAll = useCallback(async () => {
        if (!userInfo) {
            setReservationNotifications([]);
            setCommunityNotifications([]);
            setNotices([]);
            setReservationUnread(0);
            setCommunityUnread(0);
            return;
        }
        await Promise.all([
            loadReservationNotifications(),
            loadCommunityNotifications(),
            loadNotices(),
        ]);
    }, [userInfo, loadReservationNotifications, loadCommunityNotifications, loadNotices]);

    useRefreshCallback(() => {
        return loadAll();
    });

    useEffect(() => {
        loadAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userInfo]);

    const handleReservationClick = async (notification) => {
        const targetUrl = await resolveReservationNotificationUrl(notification);
        if (notification.is_read) {
            if (targetUrl) navigate(targetUrl);
            return;
        }

        const prev = [...reservationNotifications];
        const updated = reservationNotifications.map((n) =>
            n.id === notification.id ? { ...n, is_read: true } : n
        );
        setReservationNotifications(updated);
        const newUnread = updated.filter((n) => !n.is_read).length;
        setReservationUnread(newUnread);
        setReservationUnreadCount(newUnread);
        if (newUnread === 0 && communityUnread === 0) clearAppBadge();

        try {
            await markNotificationAsRead(notification.id);
        } catch {
            setReservationNotifications(prev);
            const prevUnread = prev.filter((n) => !n.is_read).length;
            setReservationUnread(prevUnread);
            setReservationUnreadCount(prevUnread);
            showError("알림 처리 중 오류가 발생했습니다");
        }

        if (targetUrl) {
            navigate(targetUrl);
        }
    };

    const handleCommunityClick = async (notification) => {
        if (!notification.is_read) {
            const prev = [...communityNotifications];
            const updated = communityNotifications.map((n) =>
                n.id === notification.id ? { ...n, is_read: true } : n
            );
            setCommunityNotifications(updated);
            const newUnread = Math.max(0, communityUnread - 1);
            setCommunityUnread(newUnread);
            setCommunityUnreadCount(newUnread);
            if (newUnread === 0 && reservationUnread === 0) clearAppBadge();

            try {
                await markCommunityNotificationAsRead(notification.id);
            } catch {
                setCommunityNotifications(prev);
                setCommunityUnread(communityUnread);
                setCommunityUnreadCount(communityUnread);
                showError("알림 처리 중 오류가 발생했습니다");
            }
        }

        const targetUrl = buildCommunityNotificationUrl(notification);

        if (targetUrl) {
            navigate(targetUrl);
        } else {
            showError("연결된 게시글을 찾을 수 없습니다");
        }
    };

    const handleNotificationClick = activeTab === "reservation" ? handleReservationClick : handleCommunityClick;

    const markReservationNotificationRead = async (notification) => {
        if (notification.is_read) return;
        const prev = [...reservationNotifications];
        const updated = reservationNotifications.map((n) =>
            n.id === notification.id ? { ...n, is_read: true } : n
        );
        setReservationNotifications(updated);
        const newUnread = updated.filter((n) => !n.is_read).length;
        setReservationUnread(newUnread);
        setReservationUnreadCount(newUnread);
        if (newUnread === 0 && communityUnread === 0) clearAppBadge();
        try {
            await markNotificationAsRead(notification.id);
        } catch {
            setReservationNotifications(prev);
            const prevUnread = prev.filter((n) => !n.is_read).length;
            setReservationUnread(prevUnread);
            setReservationUnreadCount(prevUnread);
        }
    };

    const handleActionClick = async (notification, action) => {
        if (action?.deeplink) {
            await markReservationNotificationRead(notification);
            const targetUrl = buildReservationNotificationUrl({ action_url: action.deeplink });
            if (targetUrl) navigate(targetUrl);
            return;
        }
        if (action?.api) {
            try {
                await postNotificationAction(action.api);
                showSuccess("처리되었습니다");
                await loadReservationNotifications();
            } catch (err) {
                showError(err?.response?.data?.error || "처리 중 오류가 발생했습니다");
            }
        }
    };

    const handleMarkAllRead = async () => {
        if (currentUnread === 0) return;

        if (activeTab === "reservation") {
            const prev = [...reservationNotifications];
            const prevUnread = reservationUnread;
            const updated = reservationNotifications.map((n) => ({ ...n, is_read: true }));
            setReservationNotifications(updated);
            setReservationUnread(0);
            setReservationUnreadCount(0);
            if (communityUnread === 0) clearAppBadge();

            try {
                await markAllNotificationsAsRead();
            } catch {
                setReservationNotifications(prev);
                setReservationUnread(prevUnread);
                setReservationUnreadCount(prevUnread);
                showError("전체 읽음 처리 중 오류가 발생했습니다");
            }
        } else {
            const prev = [...communityNotifications];
            const prevUnread = communityUnread;
            const updated = communityNotifications.map((n) => ({ ...n, is_read: true }));
            setCommunityNotifications(updated);
            setCommunityUnread(0);
            setCommunityUnreadCount(0);
            if (reservationUnread === 0) clearAppBadge();

            try {
                await markAllCommunityNotificationsAsRead();
            } catch {
                setCommunityNotifications(prev);
                setCommunityUnread(prevUnread);
                setCommunityUnreadCount(prevUnread);
                showError("전체 읽음 처리 중 오류가 발생했습니다");
            }
        }
    };

    const sortedDates = Object.keys(groupedNotifications).sort(
        (a, b) => new Date(b) - new Date(a)
    );

    // 공지사항: 중요 공지는 날짜 그룹과 무관하게 항상 상단 고정,
    // 나머지는 게시일(published_at) 기준으로 날짜 그룹 분리
    const importantNotices = notices.filter((n) => n.is_important);
    const normalNotices = notices.filter((n) => !n.is_important);
    const groupedNotices = groupByDate(normalNotices, "published_at");
    const sortedNoticeDates = Object.keys(groupedNotices).sort(
        (a, b) => new Date(b) - new Date(a)
    );

    return (
        <div className="page-alert">
            <SubHeader
                title="알림"
                className="alert-header"
                menus={
                    activeTab === "notice"
                        ? []
                        : [{ name: "모두 읽음", callback: handleMarkAllRead }]
                }
            />

            <div className="alert-tabs">
                <button
                    className={`alert-tab ${activeTab === "reservation" ? "active" : ""}`}
                    onClick={() => setActiveTab("reservation")}
                >
                    예약·강습
                    {reservationUnread > 0 && (
                        <span className="tab-badge">{reservationUnread}</span>
                    )}
                </button>
                <button
                    className={`alert-tab ${activeTab === "community" ? "active" : ""}`}
                    onClick={() => setActiveTab("community")}
                >
                    커뮤니티
                    {communityUnread > 0 && (
                        <span className="tab-badge">{communityUnread}</span>
                    )}
                </button>
                <button
                    className={`alert-tab ${activeTab === "notice" ? "active" : ""}`}
                    onClick={() => setActiveTab("notice")}
                >
                    공지사항
                </button>
            </div>

            <div className="alert-content">
                {activeTab === "notice" ? (
                    notices.length === 0 ? (
                        <p className="empty-state-message">공지사항이 없습니다</p>
                    ) : (
                        <>
                            {importantNotices.length > 0 && (
                                <div className="notification-group">
                                    <div className="date-group-header">
                                        <span className="date-text">중요 공지</span>
                                    </div>
                                    <div className="notice-list">
                                        {importantNotices.map((notice) => (
                                            <NoticeItem
                                                key={notice.id}
                                                notice={notice}
                                                onClick={() => window.open(`/notice/${notice.id}`, "_blank")}
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}
                            {sortedNoticeDates.map((date) => (
                                <div key={date} className="notification-group">
                                    <DateGroup date={date} />
                                    <div className="notice-list">
                                        {groupedNotices[date].map((notice) => (
                                            <NoticeItem
                                                key={notice.id}
                                                notice={notice}
                                                // 커뮤니티 게시글 상세와 동일하게 새 창/레이어로 오픈
                                                // (iOS: 새 WebView 레이어 / AOS: 동일 WebView 풀로드 / 웹: 새 탭)
                                                onClick={() => window.open(`/notice/${notice.id}`, "_blank")}
                                            />
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </>
                    )
                ) : sortedDates.length === 0 ? (
                    <p className="empty-state-message">알림이 없습니다</p>
                ) : (
                    sortedDates.map((date) => (
                        <div key={date} className="notification-group">
                            <DateGroup date={date} />
                            {groupedNotifications[date].map((notification) =>
                                activeTab === "reservation" ? (
                                    <NotificationItem
                                        key={notification.id}
                                        notification={notification}
                                        onClick={handleNotificationClick}
                                        onActionClick={handleActionClick}
                                    />
                                ) : (
                                    <CommunityNotificationItem
                                        key={notification.id}
                                        notification={notification}
                                        onClick={handleNotificationClick}
                                    />
                                )
                            )}
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

export default AlertPage;
